import React from "react";
import {
  Table,
  Typography,
  Tag,
  Space,
  Collapse,
  List,
  Avatar,
} from "antd";
import config from "../../../api_config.json";
import ParseLogo from "../../shared/images/parse.png";
import { useAppContext } from "../../../context";
import { useIsMobile } from "../../../hooks/useIsMobile";
import OfficeCard from "./OfficeCard";

const { Text } = Typography;

const CompanyOfficesTable = ({ offices }) => {
  const { isProd } = useAppContext();
  const env = isProd ? "prod" : "dev";
  const isMobile = useIsMobile();

  const columns = [
    {
      title: "Sede",
      key: "sede",
      sorter: (a, b) => (a.citta || "").localeCompare(b.citta || ""),
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.name && <Text strong>{record.name}</Text>}
          <Text>
            {record.citta || "Senza citta"}
            {record.frazione && `, ${record.frazione}`}
            {record.provincia && ` (${record.provincia})`}
          </Text>
        </Space>
      ),
    },
    {
      title: "Indirizzo",
      key: "indirizzo",
      render: (_, record) => (
        <Text>
          {record.indirizzo || "-"}
          {record.cap && ` - ${record.cap}`}
        </Text>
      ),
    },
    {
      title: "Telefoni",
      key: "telefoni",
      render: (_, record) => {
        const phones = [
          record.telefono1,
          record.telefono2,
          record.cellulare1,
          record.cellulare2,
        ].filter(Boolean);
        if (phones.length === 0) return <Text type="secondary">-</Text>;
        return (
          <Space direction="vertical" size={0}>
            {phones.map((p, i) => (
              <Text key={i}>{p}</Text>
            ))}
          </Space>
        );
      },
    },
    {
      title: "Email / PEC",
      key: "email",
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          {record.email && <Text>{record.email}</Text>}
          {record.pec && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              PEC: {record.pec}
            </Text>
          )}
          {!record.email && !record.pec && <Text type="secondary">-</Text>}
        </Space>
      ),
    },
    {
      title: "Logo",
      key: "logo",
      width: 60,
      render: (_, record) =>
        record.logo?.url ? (
          <img
            src={record.logo.url}
            alt="logo"
            style={{
              maxHeight: 30,
              maxWidth: 50,
              objectFit: "contain",
              borderRadius: 4,
            }}
          />
        ) : null,
    },
    {
      title: "Personale",
      key: "users",
      render: (_, record) => {
        const users = record.users || [];
        if (users.length === 0) {
          return <Text type="secondary">Nessun utente</Text>;
        }
        return (
          <Collapse ghost>
            <Collapse.Panel header={`${users.length} persone`} key="1">
              <List
                itemLayout="horizontal"
                dataSource={users}
                renderItem={(user) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={
                        user.profile_picture ? (
                          <Avatar src={user.profile_picture.url} />
                        ) : (
                          <Avatar>
                            {((user.first_name || "U")[0] || "").toUpperCase()}
                          </Avatar>
                        )
                      }
                      title={
                        <a
                          href={`${config.parseDashboard[env]}/browser/_User?filters=[{"field":"objectId","constraint":"eq","compareTo":"${user.objectId}"}]`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {user.first_name} {user.last_name}
                        </a>
                      }
                      description={
                        <Space direction="vertical" size={0}>
                          {user.email && <Text>{user.email}</Text>}
                          {user.telefono && <Text>{user.telefono}</Text>}
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            </Collapse.Panel>
          </Collapse>
        );
      },
    },
    {
      title: "Stato",
      key: "stato",
      width: 120,
      render: (_, record) => (
        <Space>
          <Tag color={record.enabled ? "green" : "red"}>
            {record.enabled ? "Attiva" : "Non Attiva"}
          </Tag>
          {record.nascondi && <Tag color="orange">Nascosta</Tag>}
        </Space>
      ),
    },
    {
      title: "Links",
      key: "links",
      width: 60,
      render: (_, record) => (
        <a
          target="_blank"
          rel="noopener noreferrer"
          href={`${config.parseDashboard[env]}/browser/office?filters=[{"field":"objectId","constraint":"eq","compareTo":"${record.objectId}"}]`}
        >
          <img src={ParseLogo} width={20} alt="Parse" />
        </a>
      ),
    },
  ];

  if (isMobile) {
    return (
      <div className="office-cards-container">
        {offices.map((office) => (
          <OfficeCard key={office.objectId} office={office} env={env} />
        ))}
        {offices.length === 0 && (
          <div style={{ textAlign: "center", padding: 24, color: "#999" }}>
            Nessuna sede per questa azienda
          </div>
        )}
      </div>
    );
  }

  return (
    <Table
      dataSource={offices}
      columns={columns}
      rowKey="objectId"
      pagination={false}
      scroll={{ x: true }}
      size="small"
      locale={{ emptyText: "Nessuna sede per questa azienda" }}
    />
  );
};

export default CompanyOfficesTable;
